import { apiClient, apiFetch, buildApiUrl, throwApiError } from './apiClient';
import type { GeodataInsert, GeodataUpdate } from '@urbanmetrix/sdk';

export type GeodataYamlKind = 'grid' | 'roads' | 'ground' | 'vegetation' | 'buildings' | 'targets';

export async function getGeodatas(params: { gridId?: string } = {}) {
  const response = await apiClient.api.geodatas.get({
    query: params,
  });
  if (response.error) {
    throwApiError(response.error.value, response.error.status, true);
  }
  return response.data;
}

export async function getGeodata(id: string) {
  const response = await apiClient.api.geodatas({ geodataId: id }).get();
  if (response.error) {
    throwApiError(response.error.value, response.error.status, true);
  }
  return response.data;
}

export async function addGeodata(geodata: GeodataInsert) {
  const response = await apiClient.api.geodatas.post(geodata);
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function updateGeodata(id: string, geodata: GeodataUpdate) {
  const response = await apiClient.api.geodatas({ geodataId: id }).patch(geodata);
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function replaceGeodata(id: string, geodata: GeodataInsert) {
  const response = await apiClient.api.geodatas({ geodataId: id }).put(geodata as never);
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function updateGeodataVisibility(id: string, isActive: boolean) {
  const endpoint = apiClient.api.geodatas({ geodataId: id }) as unknown as {
    visibility: {
      patch: (payload: { isActive: boolean }) => Promise<{
        data: Awaited<ReturnType<typeof getGeodata>>;
        error: { value: unknown; status: number } | null;
      }>;
    };
  };
  const response = await endpoint.visibility.patch({ isActive });
  if (response.error) throwApiError(response.error.value, response.error.status);
  return response.data;
}

export async function uploadGeodataImportedFile(
  geodataId: string,
  kind: 'customObstruction' | 'projectSketches',
  file: File
) {
  const endpoint = apiClient.api.geodatas({ geodataId }) as unknown as {
    imports: (params: { kind: string }) => {
      post: (payload: { file: File }) => Promise<{ data: unknown; error: { value: unknown; status: number } | null }>;
    };
  };
  const response = await endpoint.imports({ kind }).post({ file });
  if (response.error) throwApiError(response.error.value, response.error.status);
  return response.data;
}

export async function deleteGeodata(id: string) {
  const response = await apiClient.api.geodatas({ geodataId: id }).delete();
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export type GeodataCloneMode = 'rerun' | 'clone' | 'disabled';

type GeodataCloneLayerPayload<TInput = unknown> = { mode: GeodataCloneMode; input?: TInput };

export type CloneGeodataPayload = {
  name: string;
  gridId: string;
  description?: string | null;
  countsTowardUsage?: boolean;
  terrain: GeodataCloneLayerPayload;
  building?: GeodataCloneLayerPayload;
  vegetation?: GeodataCloneLayerPayload;
  targets?: GeodataCloneLayerPayload<unknown[]>;
};

export async function cloneGeodata(id: string, payload?: CloneGeodataPayload) {
  return apiFetch(`/api/geodatas/${id}/clone`, {
    method: 'POST',
    ...(payload
      ? {
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      : {}),
  });
}

export function downloadGeodataYaml(id: string, kind: GeodataYamlKind) {
  return buildApiUrl(`/api/geodatas/${id}/yaml?kind=${encodeURIComponent(kind)}`);
}
